import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  BookOpen,
  Coins,
  Layers,
  Megaphone,
  Play,
  Shuffle,
  Trophy,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePageTitle } from "@/hooks/usePageTitle";
import AccountNavMenu from "@/components/AccountNavMenu";

// Section card primitive - keeps every block visually consistent and
// auto-handles dark mode + mobile padding.
// `break-words` on the body container prevents long card names or
// stat values (e.g. "$1,250 in cryptocurrency value") from inducing
// horizontal scroll on narrow viewports.
function Section({ icon: Icon, color, title, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-2 border-white/40 shadow-xl rounded-3xl p-4 sm:p-6 mb-4"
    >
      <h2
        className="text-base sm:text-xl font-bold text-[#1A237E] dark:text-[#E3F2FD] flex items-start gap-2 mb-3"
        style={{ fontFamily: "Fredoka" }}
      >
        <Icon className="w-5 h-5 shrink-0 mt-0.5" style={{ color }} />
        <span className="break-words min-w-0">{title}</span>
      </h2>
      <div className="text-sm text-[#1A237E]/80 dark:text-[#E3F2FD]/80 space-y-2 leading-relaxed break-words">
        {children}
      </div>
    </section>
  );
}

function Bullet({ children, mark = "•" }) {
  return (
    <li className="flex gap-2 items-start">
      <span className="shrink-0 text-[#38B6FF] font-bold leading-relaxed">{mark}</span>
      <span className="min-w-0 break-words">{children}</span>
    </li>
  );
}

// All copy is sourced from /docs/Rulebook-v3.md. Kept English-only
// for v1 - i18n the body when we ship Spanish coverage. Section
// headers and the back-button label stay translatable.
export default function RulesPage() {
  const { t } = useTranslation();
  usePageTitle("Rules");
  const navigate = useNavigate();

  return (
    <div
      className="min-h-screen bg-[#E3F2FD] dark:bg-[#0a1428] p-4 md:p-8"
      id="main-content"
      role="main"
      tabIndex={-1}
      data-testid="rules-page"
    >
      <div className="max-w-3xl mx-auto">
        {/* Two-row header to match Profile / Settings / Season. */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            data-testid="back-to-dashboard"
            className="rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> {t("common.back")}
          </Button>
          <AccountNavMenu />
        </div>
        <h1
          className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1A237E] dark:text-[#E3F2FD] flex items-center gap-2 mb-2 break-words"
          style={{ fontFamily: "Fredoka" }}
        >
          <BookOpen className="w-6 h-6 text-[#FFC107] shrink-0" />
          {t("rules.title")}
        </h1>
        <p className="text-xs sm:text-sm text-[#1A237E]/60 dark:text-[#E3F2FD]/60 mb-6 italic">
          "Survival is the best strategy."
        </p>

        {/* ─── 1. Components ──────────────────────────────────── */}
        <Section
          icon={Layers}
          color="#8247E5"
          title={t("rules.sections.components")}
          testId="rules-components"
        >
          <p>
            Market Melee ships with <strong>165 cards</strong> across four
            decks. Each player buys, sells, and HODLs their way to a
            game-winning portfolio.
          </p>
          <ul className="space-y-1 pl-1">
            <Bullet>
              <strong>50 Cryptocurrency cards</strong> - digital assets with
              listed values.
            </Bullet>
            <Bullet>
              <strong>75 Stablecoin cards</strong> - your spending money
              ($200 / $100 / $50 denominations).
            </Bullet>
            <Bullet>
              <strong>17 Action cards</strong> - strategic plays to gain
              advantages or sabotage opponents.
            </Bullet>
            <Bullet>
              <strong>18 Event cards</strong> - market-wide impacts that shift
              the game.
            </Bullet>
            <Bullet>
              <strong>5 Market Melee cards</strong> - wild effects that
              accelerate the game's end.
            </Bullet>
          </ul>
        </Section>

        {/* ─── 2. Setup ─────────────────────────────────────────── */}
        <Section
          icon={Shuffle}
          color="#00E676"
          title={t("rules.sections.setup")}
          testId="rules-setup"
        >
          <p>
            <strong>Starting balance.</strong> Every player begins with
            $250 in stablecoins (1× $200, 1× $50) and 3 random crypto cards
            in their portfolio.
          </p>
          <p>
            <strong>Turn order.</strong> Each player draws one crypto card.
            Highest value goes first; ties break alphabetically (Bitcoin
            beats Ethereum at the same price). Return the cards and
            reshuffle once order is set. Play proceeds clockwise.
          </p>
          <p>
            <strong>Decks.</strong> Three live piles: Stablecoins (face up),
            Cryptocurrency (face down), Action / Event (face down). Keep
            separate discard piles. When a deck runs out, reshuffle its
            discard. The Market Melee discard <em>stays visible</em> - it's
            your countdown to game over.
          </p>
        </Section>

        {/* ─── 3. Turn structure ───────────────────────────────── */}
        <Section
          icon={Play}
          color="#38B6FF"
          title={t("rules.sections.turn")}
          testId="rules-turn"
        >
          <p>
            Every turn has two phases. You take exactly one Decision and
            then one Action / Event draw.
          </p>

          <div className="bg-[#E3F2FD] dark:bg-[#0a1428] rounded-2xl p-3 sm:p-4 mt-2">
            <p className="font-bold text-[#1A237E] dark:text-[#E3F2FD] mb-2">
              Phase 1 - Decision
            </p>
            <ul className="space-y-1 pl-1">
              <Bullet mark="🟢">
                <strong>Buy:</strong> draw a crypto card and pay its cost.
                Can't afford it? Discard it and skip Phase 2.
              </Bullet>
              <Bullet mark="🔴">
                <strong>Sell:</strong> sell one crypto type from your
                portfolio for stablecoins at its current value.
              </Bullet>
              <Bullet mark="🟡">
                <strong>HODL:</strong> do nothing. Your portfolio stays as
                is.
              </Bullet>
            </ul>
          </div>

          <div className="bg-[#E3F2FD] dark:bg-[#0a1428] rounded-2xl p-3 sm:p-4 mt-2">
            <p className="font-bold text-[#1A237E] dark:text-[#E3F2FD] mb-2">
              Phase 2 - Action / Event
            </p>
            <p>
              Draw one Action / Event card and resolve it immediately.{" "}
              <em>
                Skip this if you played a held card from your hand earlier
                this turn.
              </em>
            </p>
          </div>
        </Section>

        {/* ─── 4. Action / Event mechanics ─────────────────────── */}
        <Section
          icon={Zap}
          color="#FFC107"
          title={t("rules.sections.actions")}
          testId="rules-actions"
        >
          <ul className="space-y-1 pl-1">
            <Bullet>
              You can only sell <strong>one</strong> cryptocurrency type
              per turn.
            </Bullet>
            <Bullet>
              If you can't afford a buy, place it in the discard and skip
              your Action / Event phase. Turn ends immediately.
            </Bullet>
            <Bullet>
              <strong>Action cards</strong> are strategic disruptions
              (steal, freeze, swap, etc).{" "}
              <strong>Event cards</strong> hit the whole table.
            </Bullet>
            <Bullet>
              <strong>One card per turn.</strong> If you played a held
              card, you don't draw a new one this turn.
            </Bullet>
            <Bullet>
              <strong>Event ownership.</strong> If an event targets a
              crypto you don't own, discard it with no effect.
            </Bullet>
            <Bullet>
              <strong>Hand limit.</strong> Hold up to 3 Action / Event
              cards at once. Excess discards at end of turn.
            </Bullet>
          </ul>
        </Section>

        {/* ─── 5. Market Melee cards ───────────────────────────── */}
        <Section
          icon={Megaphone}
          color="#FF5A5F"
          title={t("rules.sections.melee")}
          testId="rules-melee"
        >
          <p>
            Market Melee cards are the wild deck. Each one triggers a
            global effect - a sell-off, tax, or forced swap - and counts
            toward the game's end.
          </p>
          <p>
            <strong>3 Market Melees played → game ends immediately.</strong>{" "}
            Highest cryptocurrency total wins.
          </p>
          <p className="text-xs text-[#1A237E]/60 dark:text-[#E3F2FD]/60">
            Examples: Black Swan Event, Stablecoin Tax, Wild Swap.
          </p>
        </Section>

        {/* ─── 6. Win conditions ───────────────────────────────── */}
        <Section
          icon={Trophy}
          color="#FFC107"
          title={t("rules.sections.win")}
          testId="rules-win"
        >
          <p>The first of these to happen ends the game:</p>
          <ul className="space-y-1 pl-1">
            <Bullet mark="🏆">
              <strong>Portfolio threshold.</strong> First to{" "}
              <strong>$1,250</strong> in crypto value wins immediately.
              Stablecoins don't count toward this.
            </Bullet>
            <Bullet mark="⚡">
              <strong>Market Melee mayhem.</strong> 3 Market Melee cards
              played → highest crypto total wins.
            </Bullet>
            <Bullet mark="🪙">
              <strong>Crypto collector.</strong> Hold one of every
              cryptocurrency type at once → game ends. Highest crypto
              total wins.
            </Bullet>
          </ul>
        </Section>

        {/* ─── 7. Quick reference ──────────────────────────────── */}
        <Section
          icon={Coins}
          color="#00E676"
          title={t("rules.sections.quickRef")}
          testId="rules-quickref"
        >
          {/* 2 cols on phones, 4 across on sm+ so it stays a clean
              cheat-sheet row on tablets/desktop. */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <div className="bg-[#E3F2FD] dark:bg-[#0a1428] rounded-xl p-3">
              <p className="font-bold text-[#1A237E] dark:text-[#E3F2FD] mb-1">
                Win at
              </p>
              <p className="text-[#38B6FF] font-bold text-lg">$1,250</p>
            </div>
            <div className="bg-[#E3F2FD] dark:bg-[#0a1428] rounded-xl p-3">
              <p className="font-bold text-[#1A237E] dark:text-[#E3F2FD] mb-1">
                Alternate Ending
              </p>
              <p className="text-[#FF5A5F] font-bold text-lg">3 Melees</p>
            </div>
            <div className="bg-[#E3F2FD] dark:bg-[#0a1428] rounded-xl p-3">
              <p className="font-bold text-[#1A237E] dark:text-[#E3F2FD] mb-1">
                Hand limit
              </p>
              <p className="text-[#8247E5] font-bold text-lg">3 Action/Event Cards</p>
            </div>
          </div>
        </Section>
      </div>
    </div>
  );
}
